import type { ShipmentRow } from "@/lib/database.types";
import { trackingFromShipment } from "@/lib/orderTracking";
import { CheckIcon } from "@/components/icons";

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Order-stages infographic: Ordered → Shipped → In transit → Delivered.
 * Reached stages are green with a check; the current stage pulses gold;
 * upcoming stages are muted. Pure display — data shape comes from
 * lib/orderTracking so the Bask headless API can slot in later.
 */
export function OrderTracker({
  shipment,
  medication,
}: {
  shipment: ShipmentRow;
  medication?: string;
}) {
  const t = trackingFromShipment(shipment);

  return (
    <div>
      {(medication || t.exception) && (
        <div className="flex items-center justify-between gap-3 mb-3">
          {medication && (
            <div className="text-[14px] font-semibold text-fh-ink">{medication}</div>
          )}
          {t.exception && (
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800">
              {t.exception === "returned" ? "Returned to pharmacy" : "Delivery issue"}
            </span>
          )}
        </div>
      )}

      {/* Stage rail */}
      <div className="flex items-start">
        {t.stages.map((stage, i) => {
          const isCurrent = i === t.currentIndex && !stage.at && stage.reached;
          const done = stage.reached && !isCurrent;
          return (
            <div key={stage.key} className="flex-1 flex flex-col items-center relative">
              {/* connector to the previous node */}
              {i > 0 && (
                <div
                  aria-hidden="true"
                  className={[
                    "absolute top-[13px] right-1/2 w-full h-[3px] -z-0",
                    t.stages[i]!.reached ? "bg-fh-green" : "bg-fh-border",
                  ].join(" ")}
                />
              )}
              <div
                className={[
                  "relative z-10 w-7 h-7 rounded-full flex items-center justify-center border-2",
                  done
                    ? "bg-fh-green border-fh-green text-white"
                    : isCurrent
                      ? "bg-white border-fh-gold text-fh-gold"
                      : "bg-white border-fh-border text-fh-gray-light",
                ].join(" ")}
              >
                {done ? (
                  <CheckIcon size={13} />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-current inline-block" />
                )}
              </div>
              <div
                className={[
                  "mt-1.5 text-[11.5px] font-semibold text-center leading-tight",
                  stage.reached ? "text-fh-ink" : "text-fh-gray-light",
                ].join(" ")}
              >
                {stage.label}
              </div>
              {fmtDate(stage.at) && (
                <div className="text-[10.5px] text-fh-gray">{fmtDate(stage.at)}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Carrier + tracking link */}
      {(t.carrier || t.trackingNumber) && (
        <div className="mt-3.5 pt-3 border-t border-fh-border flex items-center justify-between gap-3 flex-wrap text-[12.5px]">
          <span className="text-fh-gray">
            {t.carrier ?? "Carrier"}
            {t.trackingNumber && (
              <> · <span className="font-semibold text-fh-ink">{t.trackingNumber}</span></>
            )}
          </span>
          {t.trackingUrl && (
            <a
              href={t.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-fh-green hover:text-fh-green-dark"
            >
              Track with carrier →
            </a>
          )}
        </div>
      )}
    </div>
  );
}
